import {
  chakra,
  ColorProps,
  Flex,
  FlexProps,
  TextProps,
  useToken,
} from '@chakra-ui/react'
import React from 'react'

import { useTranslations } from '../../hooks/useTranslations'
import { useUom } from '../../hooks/useUom'
import { PriceTier } from '../../transforms'
import { formatPrice } from '../../utils/price-utils'
import { LeadText, StandardText, TinyText, TypographyProps } from '..'

type Align = 'top' | 'center'
export interface PriceProps {
  align?: Align
  alignCurrencyAndNumeric?: string
  bold?: boolean
  children: PriceTier['price']
  color?: ColorProps['color']
  currencyFontSize?: 'small' | 'normal' | 'large'
  isMultiplePricesAvailable?: boolean
  numericFontSize?: 'small' | 'normal' | 'large'
  quantity?: PriceTier['quantity']
}

export interface PriceLikeProps extends Omit<PriceProps, 'children'> {
  children: string | React.ReactNode
  currency?: string
  id?: string
}

interface CurrencyTagProps {
  fontSize?: number
}
const LeadInherited = chakra(LeadText)
const CurrencyTag: React.FC<CurrencyTagProps> = ({ fontSize, ...props }) => {
  const fontSizeProp = fontSize ? { fontSize: `${fontSize}px` } : {}
  return <LeadInherited {...fontSizeProp} {...props} />
}

const HeadingXl: React.FC<TextProps & TypographyProps> = props => (
  <LeadInherited fontSize="xl" {...props} />
)

interface PriceTagProps {
  align: Align
  bold: boolean
  color: ColorProps['color']
}

export const PriceTag: React.FC<PriceTagProps> = ({
  align,
  bold,
  color,
  ...props
}) => {
  const childrenColor = useToken('colors', color as string)
  const sx = {
    '*': {
      color: `${childrenColor} !important`,
    },
    '.typography-lead': {
      fontWeight: bold ? 'bold' : 'medium',
    },
    '.typography-text, .typography-lead': {
      display: 'inline',
    },
    '.typography-tiny': {
      fontWeight: bold ? 'bold' : 'medium',
      lineHeight: '1px',
    },
  }
  return (
    <Flex
      alignItems={align === 'top' ? 'flex-start' : 'center'}
      className="price-tag"
      sx={sx}
      whiteSpace="nowrap"
      {...props}
    />
  )
}

export const Price = ({ children, ...props }: PriceProps) => {
  const [currency, numeric] = formatPrice(children).split(/\s/)

  return (
    <PriceLike currency={currency} {...props}>
      {numeric}
    </PriceLike>
  )
}

export const PriceLike = ({
  align = 'center',
  alignCurrencyAndNumeric = 'baseline',
  bold = true,
  children: numeric,
  color = 'blue.50',
  currency,
  currencyFontSize = 'large',
  isMultiplePricesAvailable,
  numericFontSize = 'normal',
  quantity,
  ...props
}: PriceLikeProps) => {
  const t = useTranslations()
  const u = useUom()

  const currencyFontSizeValues = {
    large: 16,
    normal: 12,
    small: 11,
  }

  const Numeric = (numericFontSize === 'small'
    ? TinyText
    : numericFontSize === 'normal'
    ? LeadText
    : HeadingXl) as React.ElementType

  return (
    <PriceTag align={align} bold={bold} color={color} {...props}>
      {isMultiplePricesAvailable && (
        <StandardText>{t('label.from')}</StandardText>
      )}
      <Flex alignItems={alignCurrencyAndNumeric}>
        {currency && (
          <CurrencyTag fontSize={currencyFontSizeValues[currencyFontSize]}>
            {currency}
          </CurrencyTag>
        )}

        <Numeric>&nbsp;{numeric}</Numeric>

        {quantity && (
          <StandardText>{` / ${quantity.quantity} ${u(
            quantity.uom,
          )}`}</StandardText>
        )}
      </Flex>
    </PriceTag>
  )
}

export const SubtotalContainer: React.FC<FlexProps> = props => (
  <Flex fontSize="lg" justifyContent="space-between" {...props} />
)
